import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  Search,
  X,
  Users,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Sparkles,
  Phone,
  ShoppingBag,
  Calendar,
  MapPin,
  Clock,
  Lightbulb,
  Wallet,
  Receipt,
  Star,
  Bike,
  UtensilsCrossed,
  Home,
  ArrowLeft,
  Filter,
  FileDown,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { PRODUCT_IMAGES } from '../../../branding/assets';
import {
  getCrmClientePerfil,
  getCrmPedidoDetalhes,
  listCrmClienteEnderecos,
  listCrmClientes,
  listFeedbacksAtendidos,
} from './services/api';
import {
  buildCrmClientePdfReport,
  buildCrmDashboardPdfReport,
  downloadCrmPdf,
} from './utils/crmPdf';
import { downloadCrmCsv } from './utils/crmCsv';
import './CrmDashboard.css';

import {
  PAGE_SIZE,
  FILTERS,
  PROFILE_TABS,
  emptySummary,
  formatMoney,
  getEmptyDateRange,
  getCurrentWeekRange,
  getCurrentMonthRange,
  getCurrentYearRange,
  buildCrmDateParams,
  formatPeriodLabel,
  formatRelativeDate,
  countOnlineOrders,
  countSalonOrders,
  countTotalOrders,
  buildWhatsAppHref,
  getClienteDisplayName,
  getClienteInitials,
  getHealthClass,
  sortClientes,
  filterClientes,
  applyInsightFilter,
} from './utils/crmDashboardHelpers';

import {
  OrderModal,
  AddressModal,
  ExportModal,
} from './components/CrmDashboardModals';
import CrmProfileTabContent from './components/CrmProfileTabs';
import {
  buildSuggestion,
  buildTopInsights,
  CustomerAvatar,
  KpiInfo,
} from './components/CrmDashboardUi';
import CrmInsightsBar from './components/CrmInsightsBar';
import CrmClientesList from './components/CrmClientesList';


const CrmDashboard = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState(['Todos']);
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [activeScreen, setActiveScreen] = useState('clientes');
  const [activeTab, setActiveTab] = useState('Pedidos');
  const [selectedCliente, setSelectedCliente] = useState(null);
  // IDs de feedbacks negativos já marcados como "atendido" no Feedback Hero.
  const [feedbacksAtendidosIds, setFeedbacksAtendidosIds] = useState(() => new Set());
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState('');
  const [addressesModalOpen, setAddressesModalOpen] = useState(false);
  const [addressDetails, setAddressDetails] = useState([]);
  const [addressDetailsLoading, setAddressDetailsLoading] = useState(false);
  const [addressDetailsError, setAddressDetailsError] = useState('');
  const [page, setPage] = useState(1);
  const [profilePage, setProfilePage] = useState(1);
  const [insightFilter, setInsightFilter] = useState(null); // 'reativacao' | 'ativo' | null
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateRange, setDateRange] = useState(() => getEmptyDateRange());
  const [draftDateRange, setDraftDateRange] = useState(() => getEmptyDateRange());
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const searchInputRef = useRef(null);

  // Atalho de teclado: "/" foca a busca
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/') return;
      if (activeScreen !== 'clientes') return;
      const tag = (e.target?.tagName || '').toLowerCase();
      const isEditable = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (isEditable) return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeScreen]);

  // Resetar página ao buscar ou filtrar
  useEffect(() => {
    setPage(1);
  }, [search, activeFilters, sort, insightFilter, dateRange.start, dateRange.end]);

  // Resetar paginação das tabs ao trocar de tab ou de cliente
  useEffect(() => {
    setProfilePage(1);
  }, [activeTab, selectedCliente?.id, selectedCliente?.phone]);

  useEffect(() => {
    let active = true;

    const loadOrderDetails = async () => {
      if (!selectedOrder?.id) {
        setSelectedOrderDetails(null);
        setOrderDetailsLoading(false);
        setOrderDetailsError('');
        return;
      }

      setSelectedOrderDetails(null);
      setOrderDetailsError('');
      setOrderDetailsLoading(true);

      try {
        const details = await getCrmPedidoDetalhes(selectedOrder.id, {
          source: selectedOrder.source,
        });
        if (!active) return;
        setSelectedOrderDetails(details || null);
      } catch (error) {
        if (!active) return;
        console.error('Erro ao carregar itens do pedido CRM:', error);
        setOrderDetailsError('Não foi possível carregar os itens do pedido.');
      } finally {
        if (active) setOrderDetailsLoading(false);
      }
    };

    void loadOrderDetails();

    return () => {
      active = false;
    };
  }, [selectedOrder?.id, selectedOrder?.source]);

  useEffect(() => {
    let active = true;

    const loadClientes = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const response = await listCrmClientes({
          limit: 200,
          ordenar_por: 'ultima_interacao',
          ordem: 'desc',
          ...buildCrmDateParams(dateRange),
        });
        if (!active) return;
        const data = Array.isArray(response?.data) ? response.data : [];
        setClientes(data);
        setSummary(response?.summary || emptySummary);
      } catch (error) {
        if (!active) return;
        console.error('Erro ao carregar CRM:', error);
        setErrorMessage(error?.response?.data?.error || 'Erro ao carregar clientes do CRM.');
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadClientes();

    return () => {
      active = false;
    };
  }, [dateRange]);

  const visibleClientes = useMemo(
    () => sortClientes(
      applyInsightFilter(filterClientes(clientes, search, activeFilters), insightFilter),
      sort,
    ),
    [activeFilters, clientes, search, sort, insightFilter]
  );

  // Contagem por filtro (ignora termo de busca para refletir o universo total)
  const filterCounts = useMemo(() => {
    const counts = {};
    FILTERS.forEach((f) => {
      counts[f] = filterClientes(clientes, '', [f]).length;
    });
    return counts;
  }, [clientes]);

  // Paginação client-side
  const totalPages = Math.max(1, Math.ceil(visibleClientes.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const paginatedClientes = visibleClientes.slice(pageStart, pageEnd);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const result = [1];
    const startWindow = Math.max(2, safePage - 1);
    const endWindow = Math.min(totalPages - 1, safePage + 1);
    if (startWindow > 2) result.push('…');
    for (let i = startWindow; i <= endWindow; i += 1) result.push(i);
    if (endWindow < totalPages - 1) result.push('…');
    result.push(totalPages);
    return result;
  }, [totalPages, safePage]);

  const topInsights = useMemo(
    () => buildTopInsights(clientes, summary),
    [clientes, summary]
  );

  const handleClearSearch = useCallback(() => setSearch(''), []);

  const periodLabel = useMemo(() => formatPeriodLabel(dateRange), [dateRange]);

  const applyDateRange = (range) => {
    const nextRange = {
      start: range?.start || '',
      end: range?.end || '',
    };
    setDraftDateRange(nextRange);
    setDateRange(nextRange);
  };

  const handleApplyDateFilters = () => {
    applyDateRange(draftDateRange);
    setFiltersOpen(false);
  };

  const handleCurrentWeekFilter = () => {
    applyDateRange(getCurrentWeekRange());
  };

  const handleCurrentMonthFilter = () => {
    applyDateRange(getCurrentMonthRange());
  };

  const handleCurrentYearFilter = () => {
    applyDateRange(getCurrentYearRange());
  };

  const handleAllPeriodFilter = () => {
    applyDateRange(getEmptyDateRange());
  };

  const currentSummary = summary || emptySummary;

  const toggleFilter = (filter) => {
    setActiveFilters((current) => {
      if (filter === 'Todos') return ['Todos'];
      const withoutTodos = current.filter((item) => item !== 'Todos');
      const next = withoutTodos.includes(filter)
        ? withoutTodos.filter((item) => item !== filter)
        : [...withoutTodos, filter];
      return next.length ? next : ['Todos'];
    });
  };

  const handleSort = (key) => {
    setSort((current) => ({
      key,
      dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const openCliente = async (cliente) => {
    setSelectedCliente(cliente);
    setActiveTab('Pedidos');
    setActiveScreen('perfil');
    setAddressesModalOpen(false);
    setAddressDetails([]);
    setAddressDetailsError('');

    try {
      const perfil = await getCrmClientePerfil(
        cliente.telefone || cliente.phone,
        buildCrmDateParams(dateRange),
      );
      setSelectedCliente(perfil || cliente);
    } catch (error) {
      console.error('Erro ao carregar perfil CRM:', error);
    }

    try {
      const atendidos = await listFeedbacksAtendidos();
      setFeedbacksAtendidosIds(
        new Set((atendidos || []).map((fb) => String(fb.id))),
      );
    } catch (error) {
      console.error('Erro ao carregar feedbacks atendidos:', error);
    }
  };

  const openAddressesModal = async () => {
    const telefone = selectedCliente?.telefone || selectedCliente?.phone;
    if (!telefone || addressDetailsLoading) return;

    setAddressesModalOpen(true);
    setAddressDetails([]);
    setAddressDetailsError('');
    setAddressDetailsLoading(true);

    try {
      const enderecos = await listCrmClienteEnderecos(telefone);
      setAddressDetails(Array.isArray(enderecos) ? enderecos : []);
    } catch (error) {
      console.error('Erro ao carregar endereços do CRM:', error);
      setAddressDetailsError('Não foi possível carregar os endereços.');
    } finally {
      setAddressDetailsLoading(false);
    }
  };

  const cliente = selectedCliente;

  const handleExport = async (exportFormat) => {
    if (exporting) return;

    if (activeScreen === 'clientes' && visibleClientes.length === 0) {
      toast.warning('Não há clientes para exportar com os filtros atuais.');
      setExportModalOpen(false);
      return;
    }
    if (activeScreen === 'perfil' && !cliente) {
      toast.warning('Selecione um cliente antes de exportar.');
      setExportModalOpen(false);
      return;
    }

    setExporting(true);
    try {
      const report = activeScreen === 'perfil'
        ? buildCrmClientePdfReport({ cliente, periodLabel })
        : buildCrmDashboardPdfReport({
          clientes: visibleClientes,
          summary: currentSummary,
          periodLabel,
          activeFilters,
          search,
          insightFilter,
        });

      if (exportFormat === 'csv') {
        downloadCrmCsv(report);
      } else {
        await downloadCrmPdf(report);
      }
      toast.success(`${exportFormat.toUpperCase()} do CRM exportado com sucesso.`);
    } catch (error) {
      console.error(`Erro ao exportar ${exportFormat.toUpperCase()} do CRM:`, error);
      toast.error(`Não foi possível exportar o ${exportFormat.toUpperCase()} do CRM.`);
    } finally {
      setExporting(false);
      setExportModalOpen(false);
    }
  };

  const handleBack = () => {
    if (activeScreen === 'perfil') {
      setSelectedOrder(null);
      setSelectedOrderDetails(null);
      setAddressesModalOpen(false);
      setActiveScreen('clientes');
      return;
    }

    navigate('/products');
  };

  const clienteWhatsAppHref = buildWhatsAppHref(cliente?.telefone || cliente?.phone);

  return (
    <div className="crm-root">
      <div className="crm-app">
        <div className="crm-topbar">
          <button
            type="button"
            className="crm-back-main"
            onClick={handleBack}
            title={activeScreen === 'perfil' ? 'Voltar para dashboard do CRM' : 'Voltar para produtos'}
            aria-label={activeScreen === 'perfil' ? 'Voltar para dashboard do CRM' : 'Voltar para produtos'}
          >
            {activeScreen === 'perfil' ? (
              <ArrowLeft size={22} strokeWidth={2.2} />
            ) : (
              <Home size={22} strokeWidth={2.2} />
            )}
          </button>
          <div className="crm-brand">
            <img className="crm-logo" src={PRODUCT_IMAGES.clientCenter} alt="ClientCenter CRM" />
          </div>
          <button
            type="button"
            className="crm-export-button"
            onClick={() => setExportModalOpen(true)}
            disabled={exporting || loading || (activeScreen === 'perfil' && !cliente)}
            aria-label={activeScreen === 'perfil' ? 'Exportar perfil do cliente' : 'Exportar clientes do CRM'}
            title={activeScreen === 'perfil' ? 'Exportar perfil do cliente' : 'Exportar relatório do CRM'}
          >
            <FileDown size={17} />
            <span>{exporting ? 'Gerando arquivo...' : 'Exportar'}</span>
          </button>
        </div>

        {activeScreen === 'clientes' && (
          <div className="crm-hero">
            <h1>
              <Users size={20} color="#4F46E5" />
              Painel de clientes
            </h1>
            <p>
              Veja seus clientes, identifique quem precisa de atenção e tome decisões com
              informações claras em segundos.
            </p>
          </div>
        )}

        <section className={`crm-screen ${activeScreen === 'clientes' ? 'active' : ''}`}>
          {!loading && clientes.length > 0 && (
            <CrmInsightsBar
              topInsights={topInsights}
              insightFilter={insightFilter}
              setInsightFilter={setInsightFilter}
              onOpenCliente={openCliente}
            />
          )}

          <div className="crm-kpis">
            <div className="crm-kpi">
              <span>Clientes ativos</span>
              <strong>{currentSummary.clientes_ativos}</strong>
              <KpiInfo text="Clientes com interação recente nos últimos 30 dias." />
            </div>
            <div className="crm-kpi attn">
              <span>Precisam de atenção</span>
              <strong>{currentSummary.clientes_inativos}</strong>
              <KpiInfo text="Clientes sem interação há mais de 30 dias. Bons candidatos a uma campanha de retorno." />
            </div>
            <div className="crm-kpi">
              <span>Ticket médio delivery</span>
              <strong>{formatMoney(currentSummary.ticket_medio_delivery)}</strong>
              <KpiInfo text="Valor médio dos pedidos de delivery no período analisado." />
            </div>
            <div className="crm-kpi">
              <span>Valor total em pedidos</span>
              <strong>{formatMoney(currentSummary.valor_total_pedidos)}</strong>
              <KpiInfo text="Soma dos valores de pedidos registrados para este restaurante." />
            </div>
          </div>

          <div className="crm-panel" id="crm-clients-section">
            {insightFilter && (
              <div className="crm-insight-active-bar">
                <span className="crm-insight-active-info">
                  <Filter size={13} />
                  Filtrando por{' '}
                  <strong>
                    {insightFilter === 'reativacao'
                      ? 'clientes em janela de reativação (31–60 dias)'
                      : 'clientes ativos nos últimos 7 dias'}
                  </strong>
                </span>
                <button
                  type="button"
                  className="crm-insight-active-clear"
                  onClick={() => setInsightFilter(null)}
                >
                  <X size={12} />
                  Limpar filtro
                </button>
              </div>
            )}
            <div className="crm-filters-row">
              <div className="crm-search">
                <div className="crm-icon-btn" aria-hidden="true">
                  <Search size={16} />
                </div>
                <input
                  id="crmSearchInput"
                  ref={searchInputRef}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar por nome ou telefone…"
                  aria-label="Buscar cliente ou telefone"
                />
                {search ? (
                  <button
                    type="button"
                    className="crm-search-clear"
                    onClick={handleClearSearch}
                    aria-label="Limpar busca"
                    title="Limpar busca"
                  >
                    <X size={13} />
                  </button>
                ) : (
                  <span className="crm-search-kbd" aria-hidden="true">
                    Pressione /
                  </span>
                )}
              </div>
              <div className="crm-filters">
                {FILTERS.map((filter) => {
                  const count = filterCounts[filter];
                  const showCount = Number.isFinite(count) && filter !== 'Todos';
                  return (
                    <button
                      type="button"
                      className={`crm-chip ${activeFilters.includes(filter) ? 'active' : ''}`}
                      onClick={() => toggleFilter(filter)}
                      key={filter}
                    >
                      {filter}
                      {showCount && <span className="crm-chip-count">{count}</span>}
                    </button>
                    );
                  })}
              </div>
              <button
                type="button"
                className={`crm-filter-toggle ${filtersOpen ? 'active' : ''}`}
                onClick={() => {
                  setDraftDateRange(dateRange);
                  setFiltersOpen((current) => !current);
                }}
                aria-expanded={filtersOpen}
              >
                <Filter size={14} />
                Filtros
              </button>
              <span className="crm-period-chip">
                <Calendar size={13} />
                {periodLabel}
              </span>
            </div>

            {filtersOpen && (
              <div className="crm-date-filter-panel">
                <div className="crm-date-field">
                  <label htmlFor="crm-date-start">De</label>
                  <input
                    id="crm-date-start"
                    type="date"
                    value={draftDateRange.start}
                    onChange={(event) => setDraftDateRange((current) => ({
                      ...current,
                      start: event.target.value,
                    }))}
                  />
                </div>
                <div className="crm-date-field">
                  <label htmlFor="crm-date-end">Até</label>
                  <input
                    id="crm-date-end"
                    type="date"
                    value={draftDateRange.end}
                    onChange={(event) => setDraftDateRange((current) => ({
                      ...current,
                      end: event.target.value,
                    }))}
                  />
                </div>
                <div className="crm-date-actions">
                  <button type="button" className="crm-btn small ghost" onClick={handleAllPeriodFilter}>
                    Todo período
                  </button>
                  <button type="button" className="crm-btn small ghost" onClick={handleCurrentWeekFilter}>
                    Semana atual
                  </button>
                  <button type="button" className="crm-btn small ghost" onClick={handleCurrentMonthFilter}>
                    Mês atual
                  </button>
                  <button type="button" className="crm-btn small ghost" onClick={handleCurrentYearFilter}>
                    Ano atual
                  </button>
                  <button type="button" className="crm-btn small" onClick={handleApplyDateFilters}>
                    Aplicar
                  </button>
                </div>
              </div>
            )}

            <CrmClientesList
              loading={loading}
              errorMessage={errorMessage}
              sort={sort}
              onSort={handleSort}
              paginatedClientes={paginatedClientes}
              visibleClientes={visibleClientes}
              search={search}
              onOpenCliente={openCliente}
              pageStart={pageStart}
              pageEnd={pageEnd}
              totalPages={totalPages}
              safePage={safePage}
              pageNumbers={pageNumbers}
              onPageChange={setPage}
            />
          </div>
        </section>

        <section className={`crm-screen ${activeScreen === 'perfil' ? 'active' : ''}`}>
          {cliente && (
            <div className="crm-grid">
              <section className="crm-panel">
                <div className="crm-profile-head">
                  <div className="crm-profile-left">
                    <CustomerAvatar
                      photo={cliente.photo}
                      initials={getClienteInitials(cliente)}
                      size="profile"
                      health={getHealthClass(cliente)}
                    />
                    <div className="crm-profile-meta">
                      <div className="crm-name">{getClienteDisplayName(cliente)}</div>
                      <div className="crm-phone">
                        {cliente.phone ? (
                          <span className="crm-phone-with-icon">
                            <Phone size={11} />
                            {cliente.phone}
                          </span>
                        ) : (
                          'Sem telefone cadastrado'
                        )}
                      </div>
                      <div className="crm-profile-status">
                        <span className={`crm-status ${cliente.status === 'Inativo' ? 'risk' : 'vip'}`}>
                          {cliente.status === 'Inativo' ? (
                            <AlertTriangle size={11} />
                          ) : (
                            <CheckCircle2 size={11} />
                          )}
                          {cliente.status}
                        </span>
                        {cliente.microStatus && (
                          <span className="crm-micro">{cliente.microStatus}</span>
                        )}
                      </div>
                      {(cliente.tags || []).length > 0 && (
                        <div className="crm-tags">
                          {(cliente.tags || []).map((tag) => (
                            <span className="crm-tag" key={tag}>{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {clienteWhatsAppHref && (
                    <a
                      className="crm-whatsapp-action"
                      href={clienteWhatsAppHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Enviar mensagem via WhatsApp para ${getClienteDisplayName(cliente)}`}
                      title="Enviar mensagem via WhatsApp"
                    >
                      <MessageCircle size={20} />
                    </a>
                  )}
                </div>

                <div className="crm-kpis profile">
                  {(() => {
                    const totalSpent = Number(cliente.totalSpent || 0);
                    const totalPedidos = countTotalOrders(cliente);
                    const ticket = Number(cliente.ticket || 0);
                    const deliveryCount = countOnlineOrders(cliente);
                    const salaoCount = countSalonOrders(cliente);
                    const interacao = formatRelativeDate(cliente.lastAction?.date);
                    return (
                      <>
                        <div className={`crm-kpi ${!totalSpent ? 'empty' : ''}`}>
                          <div className="crm-kpi-head">
                            <span>Total gasto</span>
                            <div className="crm-kpi-icon"><Wallet size={14} /></div>
                          </div>
                          <strong>{formatMoney(totalSpent)}</strong>
                          <span className="crm-kpi-sub">
                            {totalSpent > 0
                              ? 'Soma de todos os pedidos'
                              : 'Ainda sem receita registrada'}
                          </span>
                        </div>

                        <div className={`crm-kpi ${!totalPedidos ? 'empty' : ''}`}>
                          <div className="crm-kpi-head">
                            <span>Total de pedidos</span>
                            <div className="crm-kpi-icon"><ShoppingBag size={14} /></div>
                          </div>
                          <strong>{totalPedidos}</strong>
                          <span className="crm-kpi-sub">
                            {totalPedidos > 0
                              ? `${deliveryCount} delivery · ${salaoCount} salão`
                              : 'Nenhum pedido registrado'}
                          </span>
                        </div>

                        <div className={`crm-kpi ${!ticket ? 'empty' : ''}`}>
                          <div className="crm-kpi-head">
                            <span>Ticket médio</span>
                            <div className="crm-kpi-icon"><Receipt size={14} /></div>
                          </div>
                          <strong>{formatMoney(ticket)}</strong>
                          <span className="crm-kpi-sub">
                            {ticket > 0 ? 'Média por pedido' : 'Sem base para cálculo'}
                          </span>
                        </div>

                        <div className={`crm-kpi ${!cliente.lastAction?.date ? 'empty' : ''}`}>
                          <div className="crm-kpi-head">
                            <span>Última interação</span>
                            <div className="crm-kpi-icon"><Clock size={14} /></div>
                          </div>
                          <strong>{interacao.primary}</strong>
                          <span className="crm-kpi-sub">
                            {interacao.secondary || 'Sem registro recente'}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="crm-stats">
                  {(() => {
                    const items = [
                      {
                        label: 'Reservas',
                        value: (cliente.reservas || []).length,
                        icon: Calendar,
                        bg: '#EEF2FF',
                        fg: '#4F46E5',
                      },
                      {
                        label: 'Check-ins',
                        value: (cliente.visitas || []).length,
                        icon: MapPin,
                        bg: '#F5F3FF',
                        fg: '#7C3AED',
                      },
                      {
                        label: 'Feedbacks',
                        value: (cliente.feedbacks || []).length,
                        icon: Star,
                        bg: '#FFFBEB',
                        fg: '#D97706',
                      },
                      {
                        label: 'Delivery',
                        value: countOnlineOrders(cliente),
                        icon: Bike,
                        bg: '#F0FDFA',
                        fg: '#0D9488',
                      },
                      {
                        label: 'Salão',
                        value: countSalonOrders(cliente),
                        icon: UtensilsCrossed,
                        bg: '#F0FDFA',
                        fg: '#115E59',
                      },
                    ];
                    return items.map((s) => {
                      const Icon = s.icon;
                      const isEmpty = !s.value;
                      return (
                        <div
                          className={`crm-stat ${isEmpty ? 'empty' : ''}`}
                          key={s.label}
                          style={{ '--stat-bg': s.bg, '--stat-fg': s.fg }}
                        >
                          <div className="crm-stat-head">
                            <div className="crm-stat-icon">
                              <Icon size={11} />
                            </div>
                            <strong>{s.value}</strong>
                          </div>
                          <span className="crm-stat-label">{s.label}</span>
                        </div>
                      );
                    });
                  })()}
                </div>

                <div className="crm-tabs">
                  {(() => {
                    const tabCounts = {
                      Pedidos: countTotalOrders(cliente),
                      Visitas: (cliente.visitas || []).length,
                      Feedback: (cliente.feedbacks || []).length,
                      Reservas: (cliente.reservas || []).length,
                      Dados: null,
                    };
                    return PROFILE_TABS.map((tab) => {
                      const count = tabCounts[tab];
                      const showCount = Number.isFinite(count) && count > 0;
                      return (
                        <button
                          type="button"
                          className={`crm-tab ${activeTab === tab ? 'active' : ''}`}
                          onClick={() => setActiveTab(tab)}
                          key={tab}
                        >
                          {tab}
                          {showCount && <span className="crm-tab-count">{count}</span>}
                        </button>
                      );
                    });
                  })()}
                </div>
                <div className="crm-tab-content">
                  {cliente ? (
                    <CrmProfileTabContent
                      cliente={cliente}
                      activeTab={activeTab}
                      profilePage={profilePage}
                      setProfilePage={setProfilePage}
                      onSelectOrder={setSelectedOrder}
                      feedbacksAtendidosIds={feedbacksAtendidosIds}
                      onOpenAddresses={openAddressesModal}
                    />
                  ) : null}
                </div>
              </section>

              <aside className="crm-panel">
                <h3 className="crm-aside-title">
                  <Sparkles size={16} color="#4F46E5" />
                  Centro de decisão
                </h3>
                <p className="crm-aside-sub">
                  Insights e ações sugeridas para este cliente.
                </p>

                {/* Sugestão de ação — destaque principal */}
                {(() => {
                  const sug = buildSuggestion(cliente);
                  if (!sug) return null;
                  const SugIcon = sug.icon;
                  return (
                    <div className="crm-suggestion">
                      <div className="crm-suggestion-head">
                        <Lightbulb size={13} />
                        {sug.label}
                      </div>
                      <div className="crm-suggestion-title">
                        <SugIcon size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px', color: '#4F46E5' }} />
                        {sug.title}
                      </div>
                      <div className="crm-suggestion-sub">{sug.sub}</div>
                      {clienteWhatsAppHref && (
                        <a
                          className="crm-suggestion-cta"
                          href={clienteWhatsAppHref}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <MessageCircle size={14} />
                          Mandar mensagem
                        </a>
                      )}
                    </div>
                  );
                })()}

                <div className="crm-decision-title">
                  <AlertTriangle size={11} />
                  Atenção imediata
                </div>
                {cliente.status === 'Inativo' ? (
                  <div className="crm-alert">
                    <AlertTriangle size={13} />
                    Cliente inativo. Bom momento para uma reativação.
                  </div>
                ) : (
                  <div className="crm-read">
                    <CheckCircle2 size={13} color="#0D9488" />
                    Sem alertas críticos
                  </div>
                )}

                <div className="crm-decision-title">
                  <TrendingUp size={11} />
                  Insights principais
                </div>
                {(cliente.dados?.intelligence || ['Cliente recorrente']).slice(0, 4).map((insight) => (
                  <div className="crm-insight" key={insight}>
                    <Sparkles size={13} />
                    {insight}
                  </div>
                ))}
              </aside>
            </div>
          )}
        </section>
      </div>
      <OrderModal
        order={selectedOrder}
        cliente={cliente}
        details={selectedOrderDetails}
        detailsLoading={orderDetailsLoading}
        detailsError={orderDetailsError}
        onClose={() => setSelectedOrder(null)}
      />
      <AddressModal
        open={addressesModalOpen}
        addresses={addressDetails}
        loading={addressDetailsLoading}
        error={addressDetailsError}
        onClose={() => setAddressesModalOpen(false)}
      />
      <ExportModal
        open={exportModalOpen}
        exporting={exporting}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExport}
      />
    </div>
  );
};

export default CrmDashboard;
